import type { ReactNode } from "react";
import { SlideStatement } from "../../../SlideStatement";
import { SlideVerdict } from "../../../SlideVerdict";

export default function BusinessModelUnitEconomics() {
  return (
    <div className="flex w-full flex-col px-6 pt-8 pb-4 text-slate-900 md:h-full md:px-24 md:pt-16 md:pb-8 md:[&_header]:mb-0">
      <SlideStatement eyebrow="Economics">
        <div>
          <h1 className="text-3xl font-semibold leading-[1.05] tracking-tight md:text-7xl">
            Expert ASO at the price of a tool.
          </h1>
          {/* Restates what the claim means — above the rule with it. */}
          <p className="mt-4 text-xl font-medium tracking-tight text-slate-600 md:mt-5 md:text-2xl">
            The deliverable is expert quality ASO ops — continuous, every-app,
            every-market ASO — at self-serve SaaS pricing.
          </p>
        </div>
      </SlideStatement>

      {/* Below the rule — the unit economics, then why they hold at scale. */}
      <div className="flex flex-col gap-4 md:min-h-0 md:flex-1 md:gap-4 md:pt-6">

        <div className="grid grid-cols-1 items-start gap-5 md:min-h-0 md:grid-cols-3 md:gap-6">
        <Card label="Pricing tiers (live)">
          <table className="mt-3 w-full border-collapse text-xs md:text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-[10px] font-semibold uppercase tracking-wide text-slate-500 md:text-xs">
                <th className="pb-1.5 text-left">Tier</th>
                <th className="pb-1.5 text-right">Price/mo</th>
              </tr>
            </thead>
            <tbody className="text-slate-800">
              <PricingRow tier="Free" price="€0" />
              <PricingRow tier="Basic" price="€49" />
              <PricingRow tier="Pro" price="€149" />
              <PricingRow tier="Elite" price="€399" />
              <PricingRow tier="Enterprise" price="Flexible" />
            </tbody>
          </table>
          <p className="mt-3 text-xs text-slate-500 md:text-sm">
            Annual billing: 17% off. Self-serve signup; free trial; Stripe
            billing; 30% affiliate revenue share.
          </p>
        </Card>

        <Card label="Unit economics (model)">
          <dl className="mt-3 space-y-2 text-xs md:text-sm">
            <UEMetric label="Gross margin" value="≥ 85%" sub="~98% unit margin today" />
            <UEMetric label="LTV" value="€3,072" sub="€128 × 24 mo" />
            <UEMetric
              label="Self-serve CAC"
              value="€120 → ~€195"
              sub="today → m24 w/ channel saturation"
            />
            <UEMetric label="LTV:CAC" value="~16:1" sub="floor ≥ 5:1" />
            <UEMetric label="Payback" value="~1.5 mo" sub="target ≤ 8 mo" />
          </dl>
        </Card>

        <Card label="Month 24 (base plan)">
          <p className="mt-3 text-xl font-bold tracking-tight text-blue-700 md:text-2xl">
            €3.7M ARR
          </p>
          <p className="text-xs text-slate-500 md:text-sm">
            2,189 paying accounts · €309K MRR
          </p>
          <table className="mt-3 w-full border-collapse text-xs md:text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-[10px] font-semibold uppercase tracking-wide text-slate-500 md:text-xs">
                <th className="pb-1.5 text-left">Segment</th>
                <th className="pb-1.5 text-right">Accts</th>
                <th className="pb-1.5 text-right">Mo. rev</th>
              </tr>
            </thead>
            <tbody className="text-slate-800">
              <MixRow tier="Self-serve (€128 ARPU)" accts="2,163" rev="€277K" />
              <MixRow tier="Enterprise (€1,250)" accts="26" rev="€32K" />
            </tbody>
          </table>
          <p className="mt-3 text-xs text-slate-500 md:text-sm">
            Enterprise ≈ 10% of MRR on ~1% of accounts. From the operating
            model, growing 2.8× YoY at that point.
          </p>
        </Card>
      </div>

      </div>

      <SlideVerdict>
        Exceptional value to the customer,
        <br />
        at a margin that funds our own growth.
      </SlideVerdict>
    </div>
  );
}

function Card({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col rounded-lg bg-slate-50 p-4 ring-1 ring-slate-200 md:p-4">
      <p className="text-sm font-semibold uppercase tracking-[0.15em] text-blue-700 md:text-base">
        {label}
      </p>
      {children}
    </div>
  );
}

function PricingRow({ tier, price }: { tier: string; price: string }) {
  return (
    <tr className="border-b border-slate-100 last:border-b-0">
      <td className="py-1.5">{tier}</td>
      <td className="py-1.5 text-right tabular-nums">{price}</td>
    </tr>
  );
}

function MixRow({
  tier,
  accts,
  rev,
}: {
  tier: string;
  accts: string;
  rev: string;
}) {
  return (
    <tr className="border-b border-slate-100 last:border-b-0">
      <td className="py-1.5">{tier}</td>
      <td className="py-1.5 text-right tabular-nums">{accts}</td>
      <td className="py-1.5 text-right tabular-nums">{rev}</td>
    </tr>
  );
}

function UEMetric({
  label,
  value,
  sub,
}: {
  label: string;
  value: string;
  sub: string;
}) {
  return (
    <div className="flex items-baseline justify-between gap-2 border-b border-slate-200 pb-2 last:border-b-0">
      <div>
        <dt className="font-medium text-slate-700">{label}</dt>
        <dd className="text-[10px] text-slate-500 md:text-xs">{sub}</dd>
      </div>
      <span className="tabular-nums font-semibold text-slate-900">
        {value}
      </span>
    </div>
  );
}
